/* eslint-disable no-param-reassign */
import type React from 'react';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { AppThunk } from '../app/redux/getReduxStore';
import type { UnderlayType } from '../components/ModalContainer';
import type { ToastProps } from '../components/Toast';
import type { TopicPostsOrder } from '../graphql/generated/graphql';
import { PostSort, TopicActivityAction } from '../graphql/generated/graphql';
import type { TaggedToastProps } from '../toasts/toasts';
import { persistReducer } from '../utils/persistRedux';

export type ModalContainerProps = {
  disableOutsideClose?: boolean;
  alignToTop?: boolean;
  underlayType?: UnderlayType;
  onClose?: () => void;
};

export type BaseModalProps = {
  close: () => void;
};

type ModalViewType<Props> = React.ComponentType<Props>;

// eslint-disable-next-line import/no-unused-modules -- exported for stories
export type ModalViewState<ViewProps extends BaseModalProps> = {
  isOpen: boolean;
  props?: Omit<ViewProps, 'close'> & ModalContainerProps;
  View: ModalViewType<ViewProps> | null;
};

export type ToastLevel = 'error' | 'warning' | 'success';

// eslint-disable-next-line import/no-unused-modules -- exported for stories
export interface Toast {
  id: string;
  props: ToastProps;
  tag?: string;
}

export interface UIState {
  collapsedSidebarSections: Record<string, boolean>;
  descriptionCollapsedTopics: Record<string, boolean | undefined>;
  dismissedSearchConnectionPrompts: string[];
  homeActivitiesFilter: {
    topics: 'all' | 'my';
    actions: TopicActivityAction[];
  };
  externalTopbarLoginRedirectPath?: string;
  hiddenMentionIntegrationPrompts: Record<string, boolean | undefined>;
  isIntegrationBannerHidden: boolean;
  isOrganizationSummaryCollapsed: boolean;
  isVersionMismatchModalOpen: boolean;
  isStickyTOCPreferred: boolean;
  isUserProfileCollapsed: boolean;
  modal: ModalViewState<BaseModalProps>;
  selectedSectionId: string | null;
  templatesOrder: PostSort;
  toasts: Toast[];
  topicPostsOrderBy: Record<string, TopicPostsOrder>;
}

const initialState: UIState = {
  collapsedSidebarSections: {},
  homeActivitiesFilter: {
    topics: 'all',
    actions: Object.values(TopicActivityAction),
  },
  descriptionCollapsedTopics: {},
  dismissedSearchConnectionPrompts: [],
  externalTopbarLoginRedirectPath: undefined,
  hiddenMentionIntegrationPrompts: {},
  isIntegrationBannerHidden: false,
  isOrganizationSummaryCollapsed: false,
  isVersionMismatchModalOpen: false,
  isStickyTOCPreferred: true,
  isUserProfileCollapsed: false,
  modal: {
    isOpen: false,
    props: {},
    View: null,
  },
  selectedSectionId: null,
  templatesOrder: PostSort.Popularity,
  toasts: [],
  topicPostsOrderBy: {},
};

const uiSlice = createSlice({
  name: 'uiSlice',
  initialState,
  reducers: {
    openVersionMismatchModal: (state) => {
      state.isVersionMismatchModalOpen = true;
    },
    setModal: (
      state,
      action: PayloadAction<ModalViewState<BaseModalProps>>,
    ) => {
      state.modal = action.payload;
    },
    closeModal: (
      state,
      action: PayloadAction<{ reset: boolean } | undefined>,
    ) => {
      if (action.payload?.reset) {
        state.modal = initialState.modal;
      } else {
        state.modal.isOpen = false;
      }
    },
    setSelectedSectionId: (
      state,
      { payload: selectedSectionId }: PayloadAction<string | null>,
    ) => {
      state.selectedSectionId = selectedSectionId;
    },
    hideIntegrationBanner: (state) => {
      state.isIntegrationBannerHidden = true;
    },
    setStickyTOCPreferred: (
      state,
      { payload: isStickyTOCPreferred }: PayloadAction<boolean>,
    ) => {
      state.isStickyTOCPreferred = isStickyTOCPreferred;
    },
    toggleOrganizationSummaryCollapsed: (
      state,
      { payload: isCollapsed }: PayloadAction<boolean>,
    ) => {
      state.isOrganizationSummaryCollapsed = isCollapsed;
    },
    toggleUserProfileCollapsed: (
      state,
      { payload: isCollapsed }: PayloadAction<boolean>,
    ) => {
      state.isUserProfileCollapsed = isCollapsed;
    },
    setExternalTopbarLoginRedirectPath: (
      state,
      {
        payload: externalTopbarLoginRedirectPath,
      }: PayloadAction<string | undefined>,
    ) => {
      state.externalTopbarLoginRedirectPath = externalTopbarLoginRedirectPath;
    },
    dismissSearchConnectionPrompt: (
      state,
      { payload: platform }: PayloadAction<string>,
    ) => {
      state.dismissedSearchConnectionPrompts = [
        ...state.dismissedSearchConnectionPrompts,
        platform,
      ];
    },
    changeTemplatesOrder: (
      state,
      { payload: templatesOrder }: PayloadAction<PostSort>,
    ) => {
      state.templatesOrder = templatesOrder;
    },
    changeTopicPostsOrderBy: (
      state,
      action: PayloadAction<{
        topicId: string;
        orderBy: TopicPostsOrder;
      }>,
    ) => {
      state.topicPostsOrderBy[action.payload.topicId] = action.payload.orderBy;
    },
    toggleSidebarSection: (
      state,
      {
        payload: { section, isInitiallyCollapsed },
      }: PayloadAction<{ section: string; isInitiallyCollapsed?: boolean }>,
    ) => {
      state.collapsedSidebarSections[section] =
        section in state.collapsedSidebarSections
          ? !state.collapsedSidebarSections[section]
          : !isInitiallyCollapsed;
    },
    toggleTopicDescription: (state, { payload: id }: PayloadAction<string>) => {
      if (state.descriptionCollapsedTopics[id]) {
        delete state.descriptionCollapsedTopics[id];
      } else {
        state.descriptionCollapsedTopics[id] = true;
      }
    },
    hideMentionIntegrationPrompt: (
      state,
      { payload: platform }: PayloadAction<string>,
    ) => {
      state.hiddenMentionIntegrationPrompts[platform] = true;
    },
    clearToasts: (state) => {
      state.toasts = [];
    },
    removeToast: (state, { payload: id }: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((toast) => toast.id !== id);
    },
    removeToastsByTag: (state, { payload: tag }: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((toast) => toast.tag !== tag);
    },
    pushToast: (state, { payload: toast }: PayloadAction<Toast>) => {
      state.toasts.push(toast);
    },
    toggleHomeActivitiesActions: (
      state,
      {
        payload,
      }: PayloadAction<{
        actions: TopicActivityAction[];
        isEnabled: boolean;
      }>,
    ) => {
      state.homeActivitiesFilter.actions = payload.isEnabled
        ? [
            ...new Set([
              ...state.homeActivitiesFilter.actions,
              ...payload.actions,
            ]),
          ]
        : state.homeActivitiesFilter.actions.filter(
            (item) => !payload.actions.includes(item),
          );
    },
    setHomeActivitiesTopics: (
      state,
      { payload }: PayloadAction<UIState['homeActivitiesFilter']['topics']>,
    ) => {
      state.homeActivitiesFilter.topics = payload;
    },
  },
});

type PushToastOptions = {
  duration?: number;
  tag?: string;
};

const DEFAULT_DURATION = 5000;

const pushToast =
  (
    props: TaggedToastProps,
    { duration = DEFAULT_DURATION, tag }: PushToastOptions = {
      duration: DEFAULT_DURATION,
    },
  ): AppThunk =>
  (dispatch) => {
    const id = Math.random().toString(36).slice(-4);
    if (duration > 0) {
      setTimeout(() => {
        dispatch(UIActions.removeToast(id));
      }, duration);
    }
    dispatch(uiSlice.actions.pushToast({ id, props, tag }));
    return Promise.resolve(id);
  };

const openModal =
  <Props extends BaseModalProps>(
    View: ModalViewType<Props>,
    props?: Omit<Props, 'close'> & ModalContainerProps,
  ): AppThunk =>
  (dispatch) => {
    dispatch(
      uiSlice.actions.setModal({
        View: View as ModalViewType<BaseModalProps>,
        props,
        isOpen: true,
      }),
    );
  };

export const UIActions = {
  ...uiSlice.actions,
  openModal,
  pushToast,
};

export default persistReducer(
  {
    key: 'ui',
    whitelist: [
      'collapsedSidebarSections',
      'descriptionCollapsedTopics',
      'dismissedSearchConnectionPrompts',
      'hiddenMentionIntegrationPrompts',
      'isOrganizationSummaryCollapsed',
      'isStickyTOCPreferred',
      'isUserProfileCollapsed',
      'templatesOrder',
      'topicPostsOrderBy',
      'homeActivitiesFilter',
    ],
  },
  uiSlice.reducer,
);
